import { useState } from "react";
import { fieldTemplate, type FieldType } from "../../vault/catalog";
import { type Field } from "../../vault/items";
import { type ValidationIssue } from "../../vault/validate";
import { copyEphemeral } from "../clipboard";
import { useToast } from "../toast-context";
import { useT } from "../../i18n";
import { Icons } from "../icons";
import { Generator } from "./Generator";

const SECRET_TYPES: FieldType[] = ["secret", "password", "passphrase", "psk", "totp", "otp_recovery_codes", "ssh_private_key", "private_key", "connection_string", "certificate"];
const MULTILINE_TYPES: FieldType[] = ["ssh_private_key", "ssh_public_key", "certificate", "private_key", "note"];
/** Types offered when retyping a custom field (the ones that make sense hand-authored). */
const CUSTOM_TYPES: FieldType[] = ["text", "secret", "password", "username", "host", "port", "url", "date", "note"];

/**
 * One editable field. Differences from the previous inline version:
 *  - enum options are read from THIS item type's template, not from a catalog-wide key search that
 *    picked the wrong options for keys several types share (port, password, connector, …);
 *  - template fields can no longer be removed (they'd reappear from the template anyway) — they
 *    offer "clear value" instead, so the destructive action is not the obvious one;
 *  - custom fields can finally be renamed and retyped;
 *  - the row shows its own validation message.
 */
export function FieldRow({
  itemType,
  field,
  issue,
  onChange,
  onRemove,
}: {
  itemType: string;
  field: Field;
  issue?: ValidationIssue;
  onChange: (patch: Partial<Field>) => void;
  onRemove: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [showNote, setShowNote] = useState(!!field.note);
  const [reveal, setReveal] = useState(false);
  const template = fieldTemplate(itemType, field.key);
  const isCustom = !template;
  const isSecret = field.secret ?? SECRET_TYPES.includes(field.type);
  const isMulti = field.multiline ?? MULTILINE_TYPES.includes(field.type);
  const canGenerate = field.type === "password" || field.type === "secret" || field.type === "passphrase";
  const options = template?.options;
  const invalid = issue?.severity === "error";
  const control = `w-full rounded border bg-panel px-2 py-1.5 outline-none ${invalid ? "border-danger focus:border-danger" : "border-border focus:border-accent"}`;

  const copy = () => {
    copyEphemeral(field.value);
    toast.success(`${field.label} — ${t("copied")}`);
  };

  return (
    <div className="group rounded border border-border/60 bg-panel2/40 p-2">
      <div className="mb-1 flex items-center gap-2">
        {isCustom ? (
          <>
            <input
              value={field.label}
              onChange={(e) => onChange({ label: e.target.value })}
              aria-label={t("Field name")}
              placeholder={t("Field name")}
              className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-xs text-fg outline-none hover:border-border focus:border-accent"
            />
            <select
              value={field.type}
              onChange={(e) => {
                const next = e.target.value as FieldType;
                onChange({ type: next, secret: SECRET_TYPES.includes(next), multiline: MULTILINE_TYPES.includes(next) });
              }}
              aria-label={t("Field type")}
              className="rounded border border-border bg-panel px-1 py-0.5 text-[11px] text-muted outline-none focus:border-accent"
            >
              {CUSTOM_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {t(ft)}
                </option>
              ))}
            </select>
          </>
        ) : (
          <span className="flex flex-1 items-center gap-1 text-xs text-muted">
            {t(field.label)}
            {isSecret ? <Icons.lock className="h-3 w-3" /> : null}
          </span>
        )}
        {/* Row actions: revealed on hover/focus so the form is calm, but always reachable by keyboard. */}
        <span className="flex shrink-0 items-center gap-1 opacity-60 transition group-focus-within:opacity-100 group-hover:opacity-100">
          <IconButton label={t("Add a note")} onClick={() => setShowNote((v) => !v)}>
            <Icons.info className="h-3.5 w-3.5" />
          </IconButton>
          {field.value && (
            <IconButton label={t("Copy")} onClick={copy}>
              <Icons.copy className="h-3.5 w-3.5" />
            </IconButton>
          )}
          {isCustom ? (
            <IconButton label={t("Remove field")} danger onClick={onRemove}>
              <Icons.trash className="h-3.5 w-3.5" />
            </IconButton>
          ) : (
            field.value && (
              <IconButton label={t("Clear value")} onClick={() => onChange({ value: "" })}>
                <Icons.close className="h-3.5 w-3.5" />
              </IconButton>
            )
          )}
        </span>
      </div>

      {options ? (
        <select value={field.value} onChange={(e) => onChange({ value: e.target.value })} aria-label={t(field.label)} className={control}>
          <option value="">—</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : isMulti ? (
        <textarea
          value={field.value}
          onChange={(e) => onChange({ value: e.target.value })}
          rows={field.type === "note" ? 3 : 4}
          aria-label={t(field.label)}
          className={`${control} font-mono text-xs`}
        />
      ) : isSecret ? (
        <div className="flex items-center gap-1">
          <input
            type={reveal ? "text" : "password"}
            value={field.value}
            onChange={(e) => onChange({ value: e.target.value })}
            autoComplete="off"
            aria-label={t(field.label)}
            aria-invalid={invalid || undefined}
            className={`${control} font-mono`}
          />
          <IconButton label={reveal ? t("Hide") : t("Reveal")} onClick={() => setReveal((v) => !v)} bordered>
            {reveal ? <Icons.eyeOff className="h-3.5 w-3.5" /> : <Icons.eye className="h-3.5 w-3.5" />}
          </IconButton>
        </div>
      ) : (
        <input
          type={field.type === "date" ? "date" : "text"}
          inputMode={field.type === "port" ? "numeric" : undefined}
          value={field.value}
          onChange={(e) => onChange({ value: e.target.value })}
          aria-label={t(field.label)}
          aria-invalid={invalid || undefined}
          className={control}
        />
      )}

      {canGenerate && <Generator onUse={(v) => onChange({ value: v })} />}
      {issue && (
        <div className={`mt-1 text-[11px] ${invalid ? "text-danger" : "text-warn"}`}>{t(issue.message)}</div>
      )}
      {showNote && (
        <input
          value={field.note ?? ""}
          onChange={(e) => onChange({ note: e.target.value })}
          placeholder={t("note for this field")}
          className="mt-1 w-full rounded border border-border bg-panel px-2 py-1 text-xs outline-none focus:border-accent"
        />
      )}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
  danger = false,
  bordered = false,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
  bordered?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`shrink-0 rounded px-1.5 py-1 ${bordered ? "border border-border hover:border-accent" : ""} ${
        danger ? "text-muted hover:text-danger" : "text-muted hover:text-accent"
      }`}
    >
      {children}
    </button>
  );
}
